import React, { memo } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import JobRow, { JobRowHandlers } from './JobRow';
import { BusyAction } from './hooks/useJobActions';
import { SelectAllState } from './hooks/useJobSelection';
import { DashboardJob } from './jobModel';

const COLUMNS: ReadonlyArray<{ label: string; width: string | number }> = [
  { label: 'Job', width: '14%' },
  { label: 'Partner', width: '19%' },
  { label: 'Type', width: '20%' },
  { label: 'Status', width: 136 },
  { label: 'Created', width: 112 },
  { label: 'Duration', width: 88 },
  { label: 'Files', width: 64 },
  { label: '', width: 96 },
];

const tableSx = {
  tableLayout: 'fixed',
  minWidth: 940,
  '& .MuiTableCell-root': { px: 1, borderColor: 'divider' },
  '& .MuiTableCell-head': { height: 44, py: 0, fontSize: 12, fontWeight: 600, color: 'text.secondary' },
} as const;

interface JobsTableProps extends JobRowHandlers {
  jobs: DashboardJob[];
  selectedIds: ReadonlySet<string>;
  busy: Readonly<Record<string, BusyAction>>;
  selectAllState: SelectAllState;
  onToggleAll: () => void;
}

const JobsTable: React.FC<JobsTableProps> = ({ jobs, selectedIds, busy, selectAllState, onToggleAll, ...handlers }) => (
  <TableContainer>
    <Table sx={tableSx} aria-label="Jobs">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" sx={{ width: 52 }}>
            <Checkbox
              checked={selectAllState === 'all'}
              indeterminate={selectAllState === 'some'}
              disabled={jobs.length === 0}
              onChange={onToggleAll}
              inputProps={{ 'aria-label': 'Select all visible jobs' }}
            />
          </TableCell>
          {COLUMNS.map((col, i) => (
            <TableCell key={col.label || i} sx={{ width: col.width }}>
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job) => (
          <JobRow key={job.id} job={job} selected={selectedIds.has(job.id)} busy={busy[job.id]} {...handlers} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default memo(JobsTable);
